/*
 * Author: Raul Perusquia <raul@inikoo.com>
 * Created: Tue, 12 Sep 2023 20:51:16 Malaysia Time, Sanur, Bali, Indonesia
 * Copyright (c) 2023, Raul A Perusquia Flores
 */

import "./bootstrap_iris";
import "../css/app.css";

import { createSSRApp, h } from "vue";
import { createInertiaApp, router } from "@inertiajs/vue3";
import { ZiggyVue, route as ziggyRoute } from "ziggy-js";
import { i18nVue, loadLanguageAsync } from "laravel-vue-i18n";
import Notifications from "@kyvg/vue3-notification";
import { createPinia } from "pinia";

import FloatingVue from "floating-vue";
import "floating-vue/dist/style.css";
import IrisLayout from "@/Layouts/Iris.vue";
import PrimeVue from "primevue/config";
import Aura from "@primevue/themes/aura";
import { definePreset } from "@primevue/themes";
import ConfirmationService from "primevue/confirmationservice";
import { ctrans } from "@/Composables/useTrans";
import { irisI18nOptions, loadLocaleMessages, normalizeLocale } from "@/Composables/useIrisTranslations";
import { isIosChromeInjectedRecursion, sentryDenyUrls, sentryIgnoreErrors } from "@/Composables/sentryNoise";

const MyPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50 : "{gray.50}",
      100: "{gray.100}",
      200: "{gray.200}",
      300: "{gray.300}",
      400: "{gray.400}",
      500: "{gray.500}",
      600: "{gray.600}",
      700: "{gray.700}",
      800: "{gray.800}",
      900: "{gray.900}",
      950: "{gray.950}"
    }
  }
});

const IRIS_PAGE_PRELOAD_LIMIT_MS = 3000;

const appElement = document.getElementById("app");
const initialPage = JSON.parse(document.querySelector('script[data-page="app"]')?.textContent || "null");
if (initialPage && window.location.search && new URL(initialPage.url, window.location.origin).pathname === window.location.pathname) {
  initialPage.url = window.location.pathname + window.location.search;
}
let nextPageProps = appElement?.childElementCount ? null : initialPage?.props;

router.on("before", (event) => {
  event.detail.visit.headers["X-Current-Url"] = window.location.href;
});

router.on("beforeUpdate", (event) => {
  nextPageProps = event.detail.page.props;
});

const irisLocale = normalizeLocale(document.documentElement.lang);

const irisShortDayNames = (() => {
  const sunday = new Date(2024, 0, 7);
  return Array.from({ length: 7 }, (_, offset) => {
    const day = new Date(sunday);
    day.setDate(sunday.getDate() + offset);
    try {
      return new Intl.DateTimeFormat(irisLocale, { weekday: "short" }).format(day);
    } catch {
      return day.toLocaleDateString("en", { weekday: "short" });
    }
  });
})();
const irisLocaleMessages = loadLocaleMessages(irisLocale);

createInertiaApp(
  {
    page   : initialPage ?? undefined,
    resolve: async (name) => {
        const pages = import.meta.glob([
            './Pages/Iris/**/*.{vue,js}',
            './Iris/Pages/**/*.{vue,js}',
        ])

        const path =
            pages[`./Pages/Iris/${name}.vue`] ||
            pages[`./Pages/Iris/${name}.js`] ||
            pages[`./Pages/Iris/${name}.ts`] ||
            pages[`./Iris/Pages/${name}.vue`] ||
            pages[`./Iris/Pages/${name}.ts`] ||
            pages[`./Iris/Pages/${name}.js`]

        if (!path) {
            throw new Error(`Page not found: ${name}`)
        }

        const pageProps = nextPageProps
        nextPageProps = null
        const pagePreload = pageProps
            ? import("@/Iris/Composables/getIrisComponents").then(({ preloadIrisPage }) => preloadIrisPage(pageProps))
            : null

        const page = await path()

        page.default.layout =
            page.default.layout || IrisLayout

        await Promise.race([pagePreload, new Promise((resolve) => setTimeout(resolve, IRIS_PAGE_PRELOAD_LIMIT_MS))])

        return page
    },
    async setup({ el, App, props, plugin }) {
      const localeMessages = await irisLocaleMessages;
      const app = createSSRApp({ render: () => h(App, props) });
      if (import.meta.env.VITE_SENTRY_IRIS_DSN) {
        const initSentry = () => import("@sentry/vue").then(({ init: sentryInit, browserTracingIntegration: sentryTracing }) => {
          sentryInit({
                        app,
                        dsn             : import.meta.env.VITE_SENTRY_IRIS_DSN,
                        environment     : import.meta.env.VITE_APP_ENV,
                        release         : document.querySelector('meta[name="app-release"]')?.content || undefined,
                        tracesSampleRate: 0.05,
                        ignoreErrors    : sentryIgnoreErrors,
                        denyUrls        : sentryDenyUrls,
                        beforeSend      : (event) => (isIosChromeInjectedRecursion(event, navigator.userAgent) ? null : event),
                        integrations    : [sentryTracing()]
                      });
        });
        if (document.readyState === "complete") {
          initSentry();
        } else {
          window.addEventListener("load", initSentry, { once: true });
        }
      }

      app.use(plugin);
      app.config.globalProperties.ctrans = ctrans;  // global function for
                                                    // <template> -- Custom
                                                    // translation
      const ziggyConfig = props.initialPage.props.ziggy;
      window.route = (name, params, absolute) => ziggyRoute(name, params, absolute, ziggyConfig);
      app.use(createPinia()).
        use(ZiggyVue, { ...ziggyConfig, location: new URL(ziggyConfig.location) }).
        use(Notifications).
        use(FloatingVue).
        use(ConfirmationService).
        use(PrimeVue, {
          locale: {
            firstDayOfWeek: 1,
            dayNamesMin   : irisShortDayNames
          },
          theme: {
            preset : MyPreset,
            options: {
              darkModeSelector: ".my-app-dark"  // dark mode of Primevue
              // depends on .my-add-dark in
              // <html>
            }
          }
        }).
        use(i18nVue, irisI18nOptions(irisLocale, localeMessages));

      await loadLanguageAsync(irisLocale);

      app.mount(el);

    },
    progress: {
      color: "#4B5563"
    }
  });

